import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Callout } from "components/common/Callout";
import { Button } from "components/common/Button";
import { plural } from "components/common/ImpactRow";
import { SeatsDialog } from "components/main/organizations/SeatsDialog";
import {
  canBuySeats,
  useSeatPurchase,
} from "components/main/organizations/useSeatPurchase";
import {
  fetchBillingContext,
  fetchCurrentPlan,
  type BillingContext,
  type CurrentPlan,
} from "@/lib/api/organizations";
import { seatRate } from "components/main/organizations/seatRate";

/// `used` counts members plus pending invitations.
export const SeatLimitNotice: React.FC<{
  orgId: string;
  used: number;
  limit: number;
  /// The address the refusal was about.
  email: string;
  onSeatsAdded: () => void | Promise<void>;
}> = ({ orgId, used, limit, email, onSeatsAdded }) => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<BillingContext | null>(null);
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetchBillingContext(orgId),
      fetchCurrentPlan(orgId),
    ]).then(([context, current]) => {
      if (cancelled) return;
      setBilling(context);
      setPlan(current);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const held = billing?.subscription?.seats ?? 0;
  const seats = useSeatPurchase(orgId, held, used);
  const buyable = canBuySeats(billing) && plan !== null;
  const way = buyable
    ? `Add a seat to invite ${email}, or remove a member to free one.`
    : billing?.can_manage_billing
      ? `Upgrade the plan to invite ${email}, or remove a member to free a seat.`
      : `Ask an owner to add a seat, or remove a member to free one.`;

  return (
    <>
      <Callout variant="warning" title="Every seat is taken">
        <span className="flex flex-col items-start gap-2">
          <span>
            {`This organization holds ${limit.toLocaleString()} ${plural(limit, "seat")}, and ${used.toLocaleString()} ${used === 1 ? "is" : "are"} taken by members and outstanding invitations. ${way}`}
          </span>
          {loaded &&
            (buyable ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={seats.openEditor}
                data-testid="add-seat-from-invite"
              >
                Add a seat
              </Button>
            ) : (
              billing?.can_manage_billing && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate("#plan", { replace: true })}
                  data-testid="seat-limit-see-plans"
                >
                  See plans
                </Button>
              )
            ))}
        </span>
      </Callout>
      {plan && (
        <SeatsDialog
          open={buyable && seats.open}
          onClose={seats.close}
          held={held}
          floor={seats.floor}
          wanted={seats.wanted}
          onWantedChange={seats.setWanted}
          plan={plan}
          seatRate={seatRate(plan, billing?.subscription)}
          busy={seats.busy}
          error={seats.error}
          onConfirm={() => {
            void seats.confirm().then((bought) => {
              if (bought !== null) void onSeatsAdded();
            });
          }}
        />
      )}
    </>
  );
};
